import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import timerService from '../../services/timerService';
import { toDayLabelWithLocale } from '../../utils/dateUtils';

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

/** Format a Date → "YYYY-MM-DD" for <input type="date"> */
const toInputDate = (d) => d.toISOString().slice(0, 10);

/** Format a date string/ISO → short label like "Mon 30" */
const toDayLabel = (isoDate) => {
  return toDayLabelWithLocale(isoDate);
};

/** Build an array of "YYYY-MM-DD" strings between two dates (inclusive). */
const buildDayRange = (fromDate, toDate) => {
  const days = [];
  const cur = new Date(fromDate + 'T00:00:00');
  const end = new Date(toDate + 'T00:00:00');
  while (cur <= end) {
    days.push(toInputDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

/** Aggregate raw PomodoroRecordDTOs into per-day buckets. */
const aggregateByDay = (records, days) => {
  const buckets = Object.fromEntries(
    days.map((d) => [d, { date: d, FOCUS: 0, SHORT_BREAK: 0, LONG_BREAK: 0, focusMinutes: 0 }])
  );

  records.forEach(({ mode, durationSeconds, completedAt }) => {
    const day = completedAt.slice(0, 10);
    if (buckets[day]) {
      buckets[day][mode] = (buckets[day][mode] || 0) + 1;
      if (mode === 'FOCUS') {
        buckets[day].focusMinutes += Math.round(durationSeconds / 60);
      }
    }
  });

  return days.map((d) => ({ ...buckets[d], label: toDayLabel(d) }));
};

/** Aggregate records into per-task buckets for the "By Task" view. */
const aggregateByTask = (records) => {
  const map = {};

  records
    .filter((r) => r.mode === 'FOCUS')
    .forEach(({ taskId, taskName, durationSeconds }) => {
      const key = taskId != null ? String(taskId) : '__untracked__';
      const label = taskName || 'Untracked';
      if (!map[key]) {
        map[key] = { label, sessions: 0, focusMinutes: 0, isUntracked: taskId == null };
      }
      map[key].sessions += 1;
      map[key].focusMinutes += Math.round(durationSeconds / 60);
    });

  return Object.values(map).sort((a, b) => b.sessions - a.sessions);
};

// ──────────────────────────────────────────────
// Custom Tooltip — By Day
// ──────────────────────────────────────────────
const TooltipRow = ({ color, label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem' }}>{label}</Typography>
    </Box>
    <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const focus = payload.find((p) => p.dataKey === 'FOCUS')?.value ?? 0;
  const shortBreak = payload.find((p) => p.dataKey === 'SHORT_BREAK')?.value ?? 0;
  const longBreak = payload.find((p) => p.dataKey === 'LONG_BREAK')?.value ?? 0;
  const focusMins = payload.find((p) => p.dataKey === 'focusMinutes')?.value ?? 0;
  return (
    <Box
      sx={{
        background: 'rgba(20, 10, 10, 0.92)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '10px',
        backdropFilter: 'blur(16px)',
        p: '10px 14px',
        minWidth: 160,
      }}
    >
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <TooltipRow color="#e53935" label="Focus" value={`${focus} session${focus !== 1 ? 's' : ''}`} />
        <TooltipRow color="#4ade80" label="Short Break" value={`${shortBreak} session${shortBreak !== 1 ? 's' : ''}`} />
        <TooltipRow color="#60a5fa" label="Long Break" value={`${longBreak} session${longBreak !== 1 ? 's' : ''}`} />
        {focusMins > 0 && (
          <TooltipRow color="rgba(255,200,100,0.9)" label="Focus time" value={`${focusMins} min`} />
        )}
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────
// Custom Tooltip — By Task
// ──────────────────────────────────────────────
const TaskTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box
      sx={{
        background: 'rgba(20,10,10,0.92)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '10px',
        backdropFilter: 'blur(16px)',
        p: '10px 14px',
        minWidth: 160,
      }}
    >
      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.78rem', mb: 0.5 }}>
        {d.isUntracked ? '🔓 Untracked' : `📋 ${d.label}`}
      </Typography>
      <TooltipRow color="#e53935" label="Focus sessions" value={d.sessions} />
      <TooltipRow
        color="rgba(255,200,100,0.9)"
        label="Focus time"
        value={d.focusMinutes >= 60 ? `${(d.focusMinutes / 60).toFixed(1)}h` : `${d.focusMinutes}m`}
      />
    </Box>
  );
};

// ──────────────────────────────────────────────
// Summary stat chip
// ──────────────────────────────────────────────
const StatChip = ({ emoji, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      px: 2,
      py: 1,
      minWidth: 72,
    }}
  >
    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{emoji}</Typography>
    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', mt: 0.25 }}>{value}</Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', mt: 0.1, textAlign: 'center' }}>
      {label}
    </Typography>
  </Box>
);

// ──────────────────────────────────────────────
// Date input styled for dark theme
// ──────────────────────────────────────────────
const DateInput = ({ label, value, onChange, min, max }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
    <Typography
      component="label"
      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8 }}
    >
      {label}
    </Typography>
    <Box
      component="input"
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '8px',
        color: 'white',
        fontSize: '0.8rem',
        px: 1.2,
        py: 0.7,
        outline: 'none',
        colorScheme: 'dark',
        cursor: 'pointer',
        '&:focus': { borderColor: 'rgba(229,57,53,0.6)', boxShadow: '0 0 0 2px rgba(229,57,53,0.15)' },
      }}
    />
  </Box>
);

// ──────────────────────────────────────────────
// View toggle tabs
// ──────────────────────────────────────────────
const ViewTabs = ({ view, onChange }) => (
  <Box sx={{ display: 'flex', gap: 0.75 }}>
    {[
      { key: 'day', label: 'By Day' },
      { key: 'task', label: 'By Task' },
    ].map(({ key, label }) => {
      const active = view === key;
      return (
        <Box
          key={key}
          onClick={() => onChange(key)}
          sx={{
            px: 1.5,
            py: 0.4,
            borderRadius: '20px',
            fontSize: '0.7rem',
            fontWeight: 600,
            cursor: 'pointer',
            background: active ? 'rgba(229,57,53,0.2)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${active ? 'rgba(229,57,53,0.5)' : 'rgba(255,255,255,0.1)'}`,
            color: active ? '#e57373' : 'rgba(255,255,255,0.45)',
            transition: 'all 0.2s',
            userSelect: 'none',
            '&:hover': { background: 'rgba(229,57,53,0.12)', color: '#e57373' },
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
);

// ──────────────────────────────────────────────
// By-Task chart (horizontal bar)
// ──────────────────────────────────────────────
const TaskChart = ({ data }) => {
  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ fontSize: '2rem', mb: 1 }}>🌱</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', fontWeight: 500 }}>
          No focus sessions in this period
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', mt: 0.5 }}>
          Activate a task and complete a focus session to see task analytics!
        </Typography>
      </Box>
    );
  }

  // Limit label width for readability
  const truncate = (str, max = 20) =>
    str.length > max ? str.slice(0, max - 1) + '…' : str;

  const chartData = data.map((d) => ({ ...d, shortLabel: truncate(d.label) }));
  // Reasonable chart height: 50px per row, min 200
  const chartHeight = Math.max(200, chartData.length * 52 + 40);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 4, right: 20, left: 8, bottom: 4 }}
          barCategoryGap="30%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
          <XAxis
            type="number"
            allowDecimals={false}
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="shortLabel"
            width={110}
            tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<TaskTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="sessions" name="Focus sessions" fill="#e53935" radius={[0, 4, 4, 0]} maxBarSize={18} />
        </BarChart>
      </ResponsiveContainer>

      {/* Per-task summary table */}
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {chartData.map((d) => (
          <Box
            key={d.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Typography
              sx={{
                flex: 1,
                color: d.isUntracked ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.75)',
                fontSize: '0.78rem',
                fontStyle: d.isUntracked ? 'italic' : 'normal',
              }}
              noWrap
            >
              {d.isUntracked ? '🔓 Untracked' : `📋 ${d.label}`}
            </Typography>
            <Typography sx={{ color: '#e57373', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>
              🍅 ×{d.sessions}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
              {d.focusMinutes >= 60 ? `${(d.focusMinutes / 60).toFixed(1)}h` : `${d.focusMinutes}m`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────
const AnalyticsPanel = () => {
  const todayStr = toInputDate(new Date());
  const weekAgoStr = toInputDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));

  const [fromDate, setFromDate] = useState(weekAgoStr);
  const [toDate, setToDate] = useState(todayStr);
  const [view, setView] = useState('day'); // 'day' | 'task'
  const [chartData, setChartData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    setLoading(true);
    setError(null);
    try {
      const from = new Date(fromDate + 'T00:00:00.000Z');
      const to = new Date(toDate + 'T23:59:59.999Z');
      const records = await timerService.getHistory(from, to);
      const days = buildDayRange(fromDate, toDate);
      setChartData(aggregateByDay(records, days));
      setTaskData(aggregateByTask(records));
    } catch (e) {
      console.error('Failed to load history:', e);
      setError('Failed to load session history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Summary stats ──
  const totalFocus = chartData.reduce((s, d) => s + d.FOCUS, 0);
  const totalShortBreak = chartData.reduce((s, d) => s + d.SHORT_BREAK, 0);
  const totalLongBreak = chartData.reduce((s, d) => s + d.LONG_BREAK, 0);
  const totalFocusMins = chartData.reduce((s, d) => s + d.focusMinutes, 0);
  const dayCount = chartData.length || 1;
  const avgFocusPerDay = (totalFocus / dayCount).toFixed(1);
  const hasData = totalFocus + totalShortBreak + totalLongBreak > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        mt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pb: 4,
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.65rem' }}
        >
          Focus Analytics
        </Typography>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', mt: 0.25 }}>
          📊 Session History
        </Typography>
      </Box>

      {/* ── Date Range Picker ── */}
      <Box
        sx={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '14px',
          backdropFilter: 'blur(12px)',
          p: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <DateInput label="From" value={fromDate} max={toDate} onChange={setFromDate} />
        <Typography sx={{ color: 'rgba(255,255,255,0.2)', pb: '6px', fontSize: '0.9rem' }}>→</Typography>
        <DateInput label="To" value={toDate} min={fromDate} max={todayStr} onChange={setToDate} />

        {/* Quick range pills */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', ml: 'auto' }}>
          {[
            { label: '7d', days: 6 },
            { label: '14d', days: 13 },
            { label: '30d', days: 29 },
          ].map(({ label, days }) => {
            const f = toInputDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
            const active = fromDate === f && toDate === todayStr;
            return (
              <Box
                key={label}
                onClick={() => { setFromDate(f); setToDate(todayStr); }}
                sx={{
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '20px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? 'rgba(229,57,53,0.2)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${active ? 'rgba(229,57,53,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: active ? '#e57373' : 'rgba(255,255,255,0.45)',
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  '&:hover': { background: 'rgba(229,57,53,0.12)', color: '#e57373' },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Summary Chips ── */}
      {!loading && !error && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <StatChip emoji="🍅" label="Focus sessions" value={totalFocus} />
          <StatChip
            emoji="⏱️"
            label="Focus time"
            value={totalFocusMins >= 60 ? `${(totalFocusMins / 60).toFixed(1)}h` : `${totalFocusMins}m`}
          />
          <StatChip emoji="📈" label="Daily avg" value={avgFocusPerDay} />
          <StatChip emoji="☕" label="Breaks" value={totalShortBreak + totalLongBreak} />
        </Box>
      )}

      {/* ── View Toggle + Chart Area ── */}
      <Box
        sx={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '16px',
          backdropFilter: 'blur(12px)',
          p: { xs: '16px 4px', sm: '20px 16px' },
          minHeight: 280,
        }}
      >
        {/* Toggle tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ViewTabs view={view} onChange={setView} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
          {loading ? (
            <CircularProgress size={32} sx={{ color: 'rgba(229,57,53,0.7)' }} />
          ) : error ? (
            <Typography sx={{ color: '#ef4444', fontSize: '0.8rem', textAlign: 'center', px: 2 }}>{error}</Typography>
          ) : view === 'task' ? (
            <Box sx={{ width: '100%' }}>
              <TaskChart data={taskData} />
            </Box>
          ) : !hasData ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>🌱</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', fontWeight: 500 }}>
                No sessions in this period
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', mt: 0.5 }}>
                Start the timer to record your first session!
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
                barCategoryGap="28%"
                barGap={2}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Legend
                  wrapperStyle={{ paddingTop: 12 }}
                  formatter={(value) => (
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>
                      {value === 'FOCUS' ? 'Focus' : value === 'SHORT_BREAK' ? 'Short Break' : 'Long Break'}
                    </span>
                  )}
                />
                <Bar dataKey="FOCUS" name="FOCUS" fill="#e53935" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Bar dataKey="SHORT_BREAK" name="SHORT_BREAK" fill="#4ade80" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Bar dataKey="LONG_BREAK" name="LONG_BREAK" fill="#60a5fa" radius={[4, 4, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>

      {/* ── Footer note ── */}
      {!loading && hasData && view === 'day' && (
        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem', textAlign: 'center', mt: -0.5 }}>
          🔴 Focus · 🟢 Short Break · 🔵 Long Break · bars show completed sessions per day
        </Typography>
      )}
      {!loading && view === 'task' && taskData.length > 0 && (
        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem', textAlign: 'center', mt: -0.5 }}>
          Showing focus sessions by task · 🔓 Untracked = sessions without an active task
        </Typography>
      )}
    </Box>
  );
};

export default AnalyticsPanel;
